// Texte und kleine Helfer für die Google-Search-Console-Anbindung (Monitoring Suite).
// Die Fehlercodes kommen aus der Monitoring-API (api/routes/searchconsole.js) bzw. als
// ?sc=error&sc_reason=<code> aus dem OAuth-Rücksprung.

const ERROR_TEXTS = {
    reauth_required: "Your Google connection has expired. Please reconnect Google Search Console.",
    not_connected: "Google Search Console is not connected.",
    not_configured: "The Google Search Console connection is not configured on this server. Please contact the support.",
    site_not_allowed: "This property is not available in your Google account.",
    forbidden: "Google denied access to this property.",
    rate_limited: "Google is rate limiting requests. Please try again in a minute.",
    invalid_site_url: "Please select a valid property.",
    invalid_start_date: "Please enter a valid start date.",
    invalid_end_date: "Please enter a valid end date.",
    start_after_end: "The start date must be before the end date.",
    end_date_in_future: "The end date cannot be in the future.",
    range_too_long: "Google Search Console provides at most 16 months of data.",
    access_denied: "You cancelled the Google authorization.",
    no_refresh_token: "Google did not grant offline access. Please try again and accept all requested permissions.",
    missing_code: "Google did not return an authorization code. Please try again.",
    google_error: "Google reported an error during authorization. Please try again.",
    invalid_company: "Your session is invalid. Please log in again.",
    invalid_return_to: "The return address is invalid. Please reload the page and try again.",
    upstream_error: "Google did not respond. Please try again later.",
    request_failed: "The request failed. Please check your connection and try again.",
    // Über die searchinsights-API (/gsc/*, VUE_APP_GSC_VIA_API) und den Rücksprung aus dem neuen Anmeldeablauf
    invalid_session: "Your session has expired. Please log in again.",
    not_allowed: "You are not allowed to do this for this company.",
    admin_required: "Only an admin of your company can connect or change Google Search Console.",
    gsc_unavailable: "Google Search Console is currently not available. Please try again later.",
    not_configured: "The Google Search Console connection is not configured on this server. Please contact the support.",
    property_not_allowed: "This property is not enabled for Quaro. An admin can enable it in the integration settings.",
    no_access: "Google does not grant access to this property with the connected Google account.",
    quota_exceeded: "Google is rate limiting requests. Please try again in a minute.",
    invalid_input: "The request contained an invalid value.",
    scope_missing: "Google Search Console access was not granted. Please try again and tick the Search Console permission.",
    invalid_state: "The connection request is invalid or was opened in a different browser. Please start again from Quaro.",
    state_expired: "The connection request has expired. Please start again.",
    ticket_expired: "The connection request has expired. Please start again.",
    ticket_used: "This connection request was already used. Please start again.",
    ticket_invalid: "The connection request is invalid. Please start again."
}

export const describeSearchConsoleError = (code) => ERROR_TEXTS[code] || "Something went wrong. Please try again or contact the support."

// Fehlercode aus einer axios-Antwort bzw. einem axios-Fehler ziehen.
export const searchConsoleErrorCode = (errorOrResponse) => {
    const data = (errorOrResponse && errorOrResponse.response && errorOrResponse.response.data) || (errorOrResponse && errorOrResponse.data) || null
    return (data && data.error) || "request_failed"
}

// Snackbar-Inhalt für den Rücksprung aus Google (?sc=connected | ?sc=error&sc_reason=...).
export const searchConsoleReturnNotification = (query) => {
    if (!query || !query.sc) return null
    if (query.sc === "connected") {
        return { message: "Google Search Console connected.", icon: "mdi-check-circle", color: "primary" }
    }
    return { message: describeSearchConsoleError(query.sc_reason), icon: "mdi-alert", color: "warning" }
}

// Aktuelle Seite ohne alte sc-Parameter - dorthin leitet der OAuth-Callback zurück.
export const searchConsoleReturnUrl = (href = window.location.href) => {
    const url = new URL(href)
    url.searchParams.delete("sc")
    url.searchParams.delete("sc_reason")
    url.hash = ""
    return url.toString()
}

// Umstellung auf die searchinsights-API (/gsc/*): Build-Schalter VUE_APP_GSC_VIA_API=true. Aus (Standard) bleibt alles wie bisher
// und die Seite ruft die Monitoring-API direkt auf. Der Schalter wird im selben Deploy aktiviert, in dem die Monitoring-API
// GSC_ENFORCE_INTERNAL=true bekommt - danach akzeptiert sie keine direkten Aufrufe mehr.
export const searchConsoleViaApi = () => process.env.VUE_APP_GSC_VIA_API === "true"

// Antwort von POST /gsc/status in die Form, die SearchConsoleConnection (und Aufrufer des Slots) kennen: sites sind hier die
// für Quaro freigegebenen Properties. can_manage: nur Admins dürfen verbinden, trennen und freigeben.
export const statusFromGscApi = (data) => {
    const source = data || {}
    const allowed = Array.isArray(source.allowed_properties) ? source.allowed_properties : []
    return {
        connected: source.connected === true,
        configured: source.configured !== false,
        reason: source.status === "reauth_required" ? "reauth_required" : null,
        connected_at: source.connected_at || null,
        google_email: source.google_email || null,
        can_manage: source.can_manage === true,
        // true: der AI Chat dieses Servers kennt die Search-Console-Tools (nur dann lohnt der Hinweis im Chat)
        chat_enabled: source.chat_enabled === true,
        sites: allowed.map((site_url) => ({ site_url, permission_level: null }))
    }
}

// Hinweis auf der Startseite des AI Chats - nur für Admins, nur über die searchinsights-API und nur, wenn der Chat die Tools kennt.
// Gibt null zurück, wenn nichts zu tun ist (verbunden mit freigegebener Property, kein Admin, Server nicht eingerichtet, Status unbekannt).
//   not_connected     Google ist nicht verbunden            -> Verbinden
//   reauth_required   Verbindung abgelaufen/widerrufen      -> neu verbinden
//   no_properties     verbunden, aber nichts freigegeben    -> Properties freigeben (in den Einstellungen)
export const searchConsoleChatHint = (status) => {
    if (!status || status.can_manage !== true || status.configured === false || status.chat_enabled !== true) return null
    if (status.connected) {
        return Array.isArray(status.sites) && status.sites.length ? null : "no_properties"
    }
    return status.reason === "reauth_required" ? "reauth_required" : "not_connected"
}
